// Skeletons of the closed-report view (/reportes/:id), drawn on the real
// .rp containers (page header, seal band, .rp-grid panels, .rp-facts,
// .rp-secs, .dtable, .rp-det, .rp-items) so the acta lands without a
// layout shift (ADR-0166). Static copy — panel titles, fact keys, table
// headers — renders for real; only what comes from the wire is a bar.
// None of these announce: the page frame is wrapped in PageSkeleton by
// the route, and each section skeleton in a `kind="section"` one.
import { Skel } from "../components/Skel";
import { SkelTable } from "../components/skeleton/SkelTable";

// «Recorrido ≤4 secciones»: the usual checklist size, used when the real
// section count is not known yet.
const DEFAULT_SECTIONS = 4;

const OBS_HEADERS = ["Observación", "Sección", "Fotos", "Severidad", "Estado hoy"];

/** The page header frame: eyebrow, store title and the date line. */
export function RpHeaderSkeleton() {
  return (
    <div className="page-header" style={{ marginBottom: 0 }}>
      <div className="page-header__lead">
        <div className="page-header__eyebrow mono">
          <Skel variant="text" width={260} />
        </div>
        <div className="page-header__title">
          <Skel variant="text" width={320} />
        </div>
        <div className="rp-meta">
          <Skel variant="text" width={200} />
        </div>
      </div>
    </div>
  );
}

/** «Estado general» facts: the four real keys, a bar per value. */
export function RpFactsSkeleton() {
  return (
    <div className="rp-facts" aria-hidden="true">
      {["Ítems revisados", "Observaciones", "vs. reporte anterior", "No aplica"].map((k) => (
        <div key={k}>
          <div className="rp-fact__k">{k}</div>
          <div className="rp-fact__v">
            <Skel variant="text" width="60%" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** «Resultado por sección» rows: index, name, compliance bar, counter. */
export function RpSecsSkeleton({ rows = DEFAULT_SECTIONS }: { rows?: number }) {
  return (
    <div className="rp-secs" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="rp-sec">
          <Skel variant="text" width={16} />
          <Skel variant="text" width={i % 2 === 0 ? "70%" : "55%"} />
          <span className="rp-bar" />
          <Skel variant="text" width="80%" />
        </div>
      ))}
    </div>
  );
}

/** «Detalle por sección» collapsed summaries. */
export function RpDetSkeleton({ rows = DEFAULT_SECTIONS }: { rows?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="rp-det">
          {/* The <summary> box: same flex row, gap and padding. */}
          <div className="skel-row" style={{ gap: 12, padding: "14px 18px" }}>
            <Skel variant="text" width={16} />
            <Skel variant="text" width={i % 2 === 0 ? 220 : 160} />
            <span style={{ marginLeft: "auto" }}>
              <Skel variant="text" width={80} />
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** The items of one open section, one row per item the bloque declares. */
export function RpItemsSkeleton({ rows }: { rows: number }) {
  return (
    <ul className="rp-items" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <li key={i} className="rp-item">
          <Skel variant="text" width={20} />
          <Skel variant="text" width={i % 3 === 0 ? "70%" : i % 3 === 1 ? "55%" : "62%"} />
          <Skel width={72} height={20} pill />
        </li>
      ))}
    </ul>
  );
}

/** The whole acta body under the header, while GET /reports/:id loads. */
export function RpBodySkeleton() {
  return (
    // Same column and gap as .rp: the PageSkeleton wrapper is one flex
    // child of <main className="rp">, so its children need the gap back.
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Seal band: neutral bar, never the green «cerrado» before the
          report is known to be terminal. */}
      <Skel height={42} />
      <div className="rp-grid">
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">Estado general de la tienda</div>
          </div>
          <div className="panel__body">
            <div className="rp-score">
              <Skel width={96} height={40} />
            </div>
            <div className="rp-seg" aria-hidden="true">
              {Array.from({ length: 5 }, (_, i) => (
                <span key={i} />
              ))}
            </div>
            <RpFactsSkeleton />
          </div>
        </div>
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">Resultado por sección</div>
          </div>
          <div className="panel__body" style={{ paddingTop: 6, paddingBottom: 6 }}>
            <RpSecsSkeleton />
          </div>
        </div>
      </div>
      <div className="panel">
        <div className="panel__head">
          <div className="panel__title">Observaciones levantadas</div>
        </div>
        <SkelTable rows={3} cols={OBS_HEADERS.length} headers={OBS_HEADERS} />
      </div>
      <div className="panel">
        <div className="panel__head">
          <div className="panel__title">Detalle por sección</div>
        </div>
        <RpDetSkeleton />
      </div>
    </div>
  );
}
